import i18n from '@/utils/i18n';
import React from 'react';
import { AgentAskChoice } from '@/experiences/sales-portal/domain/superintelligence/components/agent-ask-choice';
import { ASK_CHOICE_TOOL } from '@/experiences/sales-portal/domain/superintelligence/constants';
import { ChatBubble } from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { FormattedMarkdown } from '@/components/formatted-markdown';
import { MessagePart } from '@/experiences/sales-portal/domain/superintelligence/blocs/utils/agent-message-parts';
import { StyleProp, TextStyle, View, ViewStyle } from 'react-native';
import { ThreadMessage } from '@assistant-ui/core';

interface AgentMessageBubbleProps {
  message: ThreadMessage;
  parts: readonly MessagePart[];
  showTail?: boolean;
  style?: StyleProp<ViewStyle>;
}

/** Assistant text is markdown (the prompt asks for tables past three records); user text stays plain. */
export const AgentMessageBubble: React.FC<AgentMessageBubbleProps> = ({
  message,
  parts,
  showTail = true,
  style,
}) => {
  const [styles] = useStyles();
  const isUser = message.role === 'user';

  return (
    <View
      accessibilityLabel={
        isUser
          ? undefined
          : i18n.t('sales_portal.superintelligence.message_from_agent')
      }
      style={[styles.row, isUser ? styles.rowEnd : styles.rowStart, style]}
    >
      <ChatBubble showTail={showTail} variant={isUser ? 'sent' : 'received'}>
        {parts.map((part, index) => {
          if (part.type === 'text' && part.text.length > 0) {
            return isUser ? (
              <ChatBubble.Text key={`${message.id}-${index}`}>
                {part.text}
              </ChatBubble.Text>
            ) : (
              <FormattedMarkdown
                key={`${message.id}-${index}`}
                markdown={part.text}
                paragraphStyle={styles.markdownParagraph}
                textStyle={styles.markdownText}
              />
            );
          }
          if (part.type === 'tool-call' && part.toolName === ASK_CHOICE_TOOL) {
            return (
              <AgentAskChoice
                key={part.toolCallId}
                messageId={message.id}
                part={part}
              />
            );
          }
          return null;
        })}
      </ChatBubble>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  markdownParagraph: {
    marginBottom: theme.sk.spacing.spaceXsmall,
    marginTop: theme.sk.spacing.spaceXsmall,
  },
  markdownText: {
    ...(theme.sk.typography.paragraph.md as TextStyle),
    color: theme.sk.color.text.primary,
  },
  row: {
    width: '100%',
  },
  rowEnd: {
    alignItems: 'flex-end',
  },
  rowStart: {
    alignItems: 'flex-start',
  },
}));
